/**
 * mrpAdapter.ts — Backend Integration Seam for MRP Planning
 *
 * Connects the MRP Planning UI directly to MRP Core API:
 *   GET  /api/mrp                      read model (lines + materials)
 *   POST /api/mrp/suggestions/refresh  sync shortages into purchase_suggestions (source = MRP)
 *
 * Critical invariants:
 * 1. Server is 100% authoritative for all quantities (required, reserved, free, incoming, shortage, buy).
 *    Frontend MUST NOT calculate stock, shortage, or recommended buy qty.
 * 2. Identity uses material_id + normalized UOM + backend spec_key.
 * 3. Problem/unallocated lines in lines remain visible as issue rows (issue::<requirement_line_id>).
 */

import { apiFetch } from '@/services/apiFetch'
import type {
  MrpDashboardFilter,
  MrpDemandTrace,
  MrpIssueCode,
  MrpPlanningRow,
  MrpPlanningSummary,
  MrpPlanningViewModel,
  StructuredMaterialSpec,
} from '../types/mrpTypes'

export interface ServerMrpContribution {
  product_id: string
  product_code: string
  product_name?: string
  qty: number
}

export interface ServerMrpLine {
  demand_type: string
  requirement_batch_id: string
  requirement_batch_title: string
  requirement_line_id: string
  plan_line_id: string | null
  contributions?: ServerMrpContribution[]
  material_id: string | null
  material_code: string | null
  material_name: string
  uom: string
  material_spec: Record<string, unknown> | null
  material_spec_raw?: string | null
  spec_key: string
  required_qty: number
  consumed_qty?: number
  reserved_for_this_demand?: number
  physical_available?: number
  incoming_open_po?: number
  issues?: string[]
  unmet_qty?: number
  available_unreserved?: number
  reserved_for_other_demands?: number
  allocated_from_free?: number
  incoming_available?: number
  allocated_from_incoming?: number
  shortage_qty?: number
  recommended_buy_qty?: number
}

export interface ServerMrpMaterial {
  material_id: string
  material_code: string | null
  material_name: string
  uom: string
  material_spec: Record<string, unknown> | null
  material_spec_raw?: string | null
  spec_key: string
  required_qty: number
  unmet_qty: number
  physical_available: number
  reserved_total: number
  free_unreserved: number
  incoming_open_po: number
  incoming_unresolved?: boolean
  allocated_from_free?: number
  allocated_from_incoming?: number
  shortage_qty: number
  recommended_buy_qty: number
  issues?: string[]
  demand_refs?: Array<{
    demand_type: string
    requirement_batch_id: string
    requirement_line_id: string
    plan_line_id: string | null
    shortage_qty: number
    spec_key: string
  }>
}

export interface ServerMrpData {
  generated_at: string
  filter?: {
    demand_type?: string
    batch_id?: string
    plan_line_id?: string
    material_id?: string
  }
  lines: ServerMrpLine[]
  materials: ServerMrpMaterial[]
}

export interface SyncMrpSuggestionsResult {
  created: number
  updated: number
  dismissed: number
  open_material_ids?: string[]
  open_identity_keys?: string[]
}

export interface MrpBackendAdapter {
  isBackendWired: boolean
  fetchPlanningView(filter?: Partial<MrpDashboardFilter>): Promise<MrpPlanningViewModel>
  syncMrpSuggestions(): Promise<SyncMrpSuggestionsResult>
}

/**
 * Maps raw server MRP response into the frontend MrpPlanningViewModel.
 * Combines netted material rows with unallocated issue lines without client-side math.
 */
export function mapServerMrpToViewModel(
  serverData: ServerMrpData,
  fallbackFilter?: Partial<MrpDashboardFilter>,
): MrpPlanningViewModel {
  const materials = serverData?.materials || []
  const lines = serverData?.lines || []
  const allocatedLineIds = new Set<string>()

  // 1. Map netted materials from server
  const nettedRows: MrpPlanningRow[] = materials.map((m) => {
    const normUom = (m.uom || '').trim().toUpperCase()
    const specKey = m.spec_key || ''
    const rowKey = `${m.material_id}::${normUom}::${specKey || '__no_spec__'}`

    // Match demand traces from server lines on (material_id, normalized UOM, spec_key)
    const matchedLines = lines.filter((line) => {
      const match =
        line.material_id === m.material_id &&
        (line.uom || '').trim().toUpperCase() === normUom &&
        (line.spec_key || '') === specKey
      if (match) {
        allocatedLineIds.add(line.requirement_line_id)
      }
      return match
    })

    const demandTraces: MrpDemandTrace[] = matchedLines.map((l) => ({
      demand_type: l.demand_type,
      requirement_batch_id: l.requirement_batch_id,
      requirement_batch_title: l.requirement_batch_title,
      requirement_line_id: l.requirement_line_id,
      plan_line_id: l.plan_line_id,
      shortage_qty: l.shortage_qty,
      contributions: (l.contributions || []).map((c) => ({
        product_id: c.product_id,
        product_code: c.product_code,
        product_name: c.product_name,
        qty: c.qty,
      })),
    }))

    return {
      row_id: rowKey,
      material_id: m.material_id,
      material_code: m.material_code,
      material_name: m.material_name,
      uom: m.uom,
      material_spec: m.material_spec as StructuredMaterialSpec | null,
      required_qty: m.required_qty,
      reserved_qty: m.reserved_total,
      free_unreserved_qty: m.free_unreserved,
      incoming_po_qty: m.incoming_open_po,
      shortage_qty: m.shortage_qty,
      recommended_buy_qty: m.recommended_buy_qty,
      issues: (m.issues || []) as MrpIssueCode[],
      demand_traces: demandTraces,
      suggestion_source: 'MRP',
      suggested_qty: m.recommended_buy_qty,
    }
  })

  // 2. Identify unallocated / problem lines in lines array (e.g. MATERIAL_UNRESOLVED, BASE_UOM_MISSING)
  const unallocatedLines = lines.filter((l) => !allocatedLineIds.has(l.requirement_line_id))
  const issueRows: MrpPlanningRow[] = unallocatedLines.map((line) => ({
    row_id: `issue::${line.requirement_line_id}`,
    material_id: line.material_id,
    material_code: line.material_code,
    material_name: line.material_name || 'Vật tư chưa giải quyết',
    uom: line.uom,
    material_spec: line.material_spec as StructuredMaterialSpec | null,
    required_qty: line.required_qty,
    reserved_qty: line.reserved_for_this_demand ?? 0,
    free_unreserved_qty: 0,
    incoming_po_qty: 0,
    shortage_qty: 0,
    recommended_buy_qty: 0,
    issues: (line.issues && line.issues.length > 0 ? line.issues : ['OTHER']) as MrpIssueCode[],
    demand_traces: [
      {
        demand_type: line.demand_type,
        requirement_batch_id: line.requirement_batch_id,
        requirement_batch_title: line.requirement_batch_title,
        requirement_line_id: line.requirement_line_id,
        plan_line_id: line.plan_line_id,
        shortage_qty: line.shortage_qty,
        contributions: (line.contributions || []).map((c) => ({
          product_id: c.product_id,
          product_code: c.product_code,
          product_name: c.product_name,
          qty: c.qty,
        })),
      },
    ],
    suggestion_source: null,
    suggested_qty: null,
  }))

  const rows = [...nettedRows, ...issueRows]

  const summary: MrpPlanningSummary = {
    total_items: rows.length,
    shortage_count: rows.filter((r) => r.shortage_qty > 0).length,
    total_shortage_qty: rows.reduce((s, r) => s + (r.shortage_qty || 0), 0),
    incoming_po_count: rows.filter((r) => r.incoming_po_qty > 0).length,
    warning_count: rows.filter((r) => r.issues && r.issues.length > 0).length,
  }

  return {
    generated_at: serverData?.generated_at || null,
    demand_type_filter: serverData?.filter?.demand_type || fallbackFilter?.demand_type || 'ALL',
    rows,
    summary,
  }
}

class DefaultMrpAdapter implements MrpBackendAdapter {
  // Live backend connection is active
  public isBackendWired = true

  async fetchPlanningView(
    filter?: Partial<MrpDashboardFilter> & {
      batch_id?: string
      plan_line_id?: string
      material_id?: string
    },
  ): Promise<MrpPlanningViewModel> {
    const params = new URLSearchParams()
    const dt = filter?.demand_type || 'ALL'
    params.set('demand_type', dt)
    if (filter?.batch_id) params.set('batch_id', filter.batch_id)
    if (filter?.plan_line_id) params.set('plan_line_id', filter.plan_line_id)
    if (filter?.material_id) params.set('material_id', filter.material_id)

    const qs = params.toString()
    const res = await apiFetch(`/api/mrp${qs ? `?${qs}` : ''}`)

    if (!res.ok) {
      let msg = `Không thể tải dữ liệu cân đối MRP (${res.status})`
      try {
        const errJson = await res.json()
        if (errJson?.error?.message) {
          msg = errJson.error.message
        } else if (errJson?.message) {
          msg = errJson.message
        }
      } catch {
        // ignore
      }
      throw new Error(msg)
    }

    const json = (await res.json()) as { data: ServerMrpData }
    return mapServerMrpToViewModel(json.data, filter)
  }

  async syncMrpSuggestions(): Promise<SyncMrpSuggestionsResult> {
    const res = await apiFetch('/api/mrp/suggestions/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })

    if (!res.ok) {
      let msg = `Lỗi đồng bộ gợi ý mua hàng (${res.status})`
      if (res.status === 403) {
        msg = 'Bạn không có quyền cập nhật gợi ý mua hàng (yêu cầu quyền po.write)'
      } else if (res.status === 401) {
        msg = 'Phiên làm việc hết hạn. Vui lòng đăng nhập lại.'
      } else {
        try {
          const errJson = await res.json()
          if (errJson?.error?.message) {
            msg = errJson.error.message
          } else if (errJson?.message) {
            msg = errJson.message
          }
        } catch {
          // ignore
        }
      }
      throw new Error(msg)
    }

    const json = (await res.json()) as { data: SyncMrpSuggestionsResult }
    return {
      created: json.data?.created ?? 0,
      updated: json.data?.updated ?? 0,
      dismissed: json.data?.dismissed ?? 0,
      open_material_ids: json.data?.open_material_ids,
      open_identity_keys: json.data?.open_identity_keys,
    }
  }
}

// Active singleton instance
let activeAdapter: MrpBackendAdapter = new DefaultMrpAdapter()

export function getMrpAdapter(): MrpBackendAdapter {
  return activeAdapter
}

/**
 * Test seam: Allows tests to inject mock adapters without polluting production logic.
 */
export function setMrpAdapterForTesting(adapter: MrpBackendAdapter | null): void {
  activeAdapter = adapter ?? new DefaultMrpAdapter()
}
